import { useEffect, useMemo, useState } from 'react';

interface OptimizationConfigEditorProps {
  config: any;
  availableTipoFilati?: string[];
  availableConsegne?: string[];
  readOnly?: boolean;
  onChange: (next: any) => void;
  onSave?: (next: any) => Promise<void>;
  isSaving?: boolean;
}

interface NumericSuggestionInputProps {
  currentValue: number | null | undefined;
  integer?: boolean;
  percent?: boolean;
  disabled?: boolean;
  placeholder?: string;
  onCommit: (value: number) => void | Promise<void>;
}

const OBJECTIVE_KEYS = [
  'lambda_plus',
  'lambda_minus',
  'rho_profit',
  'lambda_avg_price',
  'lambda_price_slack',
  'lambda_sku_consegna_split',
  'lambda_consegna_priority',
  'lambda_consegna_fill_priority',
  'lambda_consegna_forecast_priority',
  'lambda_budget_target',
] as const;

// budget_target_soft e' la sola lever soft. balanced_share_soft e' stato rimosso dal
// backend: lo teniamo nella lista nascosti per non esporlo se resta in vecchie config.
const HIDDEN_ACTIVE_CONSTRAINTS = new Set<string>(['balanced_share_soft']);

type ConsegnaPriorityState = 'prioritized' | 'neutral' | 'avoid';
const CONSEGNA_PRIORITY_STATES: ConsegnaPriorityState[] = ['neutral', 'prioritized', 'avoid'];
const CONSEGNA_PRIORITY_LABELS: Record<ConsegnaPriorityState, string> = {
  prioritized: 'Prioritizzato',
  neutral: 'Presente',
  avoid: 'Evita',
};
const CONSEGNA_PRIORITY_CLASSES: Record<ConsegnaPriorityState, string> = {
  prioritized: 'border-emerald-300 bg-emerald-50 text-emerald-800 hover:border-emerald-700',
  neutral: 'border-gray-300 bg-white text-gray-600 hover:border-gray-700',
  avoid: 'border-rose-300 bg-rose-50 text-rose-800 hover:border-rose-700',
};
function normalizeConsegnaPriorityState(value: unknown): ConsegnaPriorityState {
  const raw = typeof value === 'object' && value !== null && 'value' in value
    ? (value as { value?: unknown }).value
    : value;
  const normalized = String(raw ?? 'neutral').trim().toLowerCase();
  if (normalized === 'prioritized' || normalized.endsWith('.prioritized')) return 'prioritized';
  if (normalized === 'avoid' || normalized.endsWith('.avoid')) return 'avoid';
  return 'neutral';
}
function nextConsegnaPriorityState(value: unknown): ConsegnaPriorityState {
  const current = normalizeConsegnaPriorityState(value);
  return CONSEGNA_PRIORITY_STATES[(CONSEGNA_PRIORITY_STATES.indexOf(current) + 1) % CONSEGNA_PRIORITY_STATES.length];
}

const ACTIVE_CONSTRAINT_DESCRIPTIONS: Record<string, string> = {
  area_constraints: 'Vincolo hard: controlla coerenza complessiva dell’area tra i negozi inclusi.',
  bounds: 'Vincolo hard: rispetta i minimi e massimi SKU impostati manualmente o importati.',
  group_capacity: 'Vincolo hard: non supera il valore Merchandising Plan previsto per ogni tipo filato.',
  group_quantity: 'Vincolo hard: non supera i pezzi Merchandising Plan previsti per ogni tipo filato.',
  group_models_unique: 'Vincolo hard: non supera il numero di modelli distinti previsto per tipo filato.',
  group_sku_count: 'Vincolo hard: non supera il numero di SKU distinti previsto per tipo filato (colonna SKU COUNT del Merchandising Plan). Attivo solo sui file che hanno quella colonna.',
  group_avg_price: 'Preferenza soft: guida il prezzo medio per tipo filato verso il Merchandising Plan, ma può deviare se serve.',
  budget: 'Vincolo hard: non supera il budget economico per consegna e negozio.',
  budget_items: 'Vincolo hard: non supera il numero massimo di pezzi previsto dal budget.',
  demand_band: 'Preferenza soft: spinge la quantità verso domanda/forecast attesa, accettando deviazioni penalizzate.',
  min_share: 'Vincolo hard: con più di 2 consegne bilancia gli SKU tra le consegne eleggibili; con 2 consegne forza la quota minima sulla prima consegna quando prevista dal forecast.',
  master_order: 'Vincolo hard: limita il mix master/non-master dentro la capienza MP secondo la quota non-master configurata.',
  min_active_skus: 'Vincolo hard: garantisce un numero minimo di SKU attivi nella soluzione.',
  percentage_colored: 'Vincolo hard: garantisce una quota minima di SKU colorati attivi.',
  no_split_small_skus: 'Vincolo hard legacy: forza lo split sopra una soglia quantità; non usarlo per ridurre gli split senza revisione.',
  minimize_sku_consegna_split: 'Preferenza soft: penalizza lo stesso SKU distribuito su troppe consegne.',
  consegna_priority: 'Preferenza soft: favorisce o penalizza consegne per tipo filato senza vietarle.',
  consegna_fill_priority: 'Preferenza soft leggera: favorisce il riempimento progressivo delle consegne iniziali senza superare preferisci/evita per tipo filato.',
  consegna_forecast_priority: 'Preferenza soft leggerissima: spinge ogni SKU verso le consegne coerenti col suo forecast trimestrale (trimestri di predizione, mapping configurabile qui sotto). Più debole di "riempimento consegne": se attivi entrambi comanda quest\'ultimo.',
  budget_target_soft: 'Preferenza soft: distribuisce le assegnazioni tra le consegne seguendo le quote del budget del negozio. Insegue il VALORE economico se e\' attivo \"budget\", i PEZZI se e\' attivo \"budget items\". Non vieta nulla: se domanda/forecast non lo consentono resta sotto. Peso regolabile con lambda_budget_target nella sezione Objective.',
};

// SPJ-946: suggerimenti trimestre (1-based) per il layout Spring/Resort; per gli altri
// layout (es. Main di AI) nessun suggerimento: senza trimestre assegnato il peso è 0.
const FORECAST_QUARTER_SUGGESTIONS: Record<string, number> = {
  'resort 1': 1,
  'resort 2': 1,
  'spring 1': 2,
  'spring 2': 3,
};
const FORECAST_QUARTER_OPTIONS = [1, 2, 3, 4] as const;

const CONSTRAINT_NUMERIC_KEYS = [
  'non_master_share',
  'min_allowed_value',
  'min_active_skus',
  'forecast_bias',
  'percentage_colored',
  'min_total_quantities_for_split',
] as const;

function isIntegerConstraintKey(key: string) {
  return [
    'min_allowed_value',
    'min_active_skus',
    'forecast_bias',
    'min_total_quantities_for_split',
  ].includes(key);
}

function isIntegerSolverKey(key: string) {
  return ['time_limit', 'num_workers'].includes(key);
}

function parseStrictNumber(raw: string, options?: { integer?: boolean; percent?: boolean }): number | null {
  const trimmed = raw.trim();
  if (!trimmed || trimmed.includes(',')) return null;

  const pattern = options?.integer ? /^-?\d+$/ : /^-?\d+(?:\.\d+)?$/;
  if (!pattern.test(trimmed)) return null;

  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed)) return null;

  if (options?.percent) return parsed / 100;
  if (options?.integer) return Math.trunc(parsed);
  return parsed;
}

function cloneConfig(value: any) {
  return JSON.parse(JSON.stringify(value ?? {}));
}

function NumericSuggestionInput({
  currentValue,
  integer = false,
  percent = false,
  disabled = false,
  placeholder,
  onCommit,
}: NumericSuggestionInputProps) {
  const [draft, setDraft] = useState('');
  const [isInvalid, setIsInvalid] = useState(false);
  const [shakeKey, setShakeKey] = useState(0);

  useEffect(() => {
    setDraft('');
    setIsInvalid(false);
  }, [currentValue]);

  const resolvedPlaceholder = placeholder ?? (currentValue === null || currentValue === undefined
    ? ''
    : percent
      ? String(Math.round(currentValue * 100))
      : String(currentValue));

  const triggerInvalidFeedback = () => {
    setIsInvalid(true);
    setShakeKey((prev) => prev + 1);
  };

  const commitDraft = () => {
    const trimmed = draft.trim();
    if (!trimmed) {
      setIsInvalid(false);
      return;
    }

    const nextValue = parseStrictNumber(trimmed, { integer, percent });
    if (nextValue === null) {
      triggerInvalidFeedback();
      return;
    }

    setIsInvalid(false);
    void onCommit(nextValue);
  };

  return (
    <div className="space-y-1">
      <div key={shakeKey} className={`flex items-center gap-2 ${isInvalid ? 'motion-safe:animate-[shake_0.24s_ease-in-out]' : ''}`}>
        <input
          type="text"
          inputMode={integer ? 'numeric' : 'decimal'}
          spellCheck={false}
          value={draft}
          placeholder={resolvedPlaceholder}
          disabled={disabled}
          onChange={(e) => {
            setDraft(e.target.value);
            if (isInvalid) setIsInvalid(false);
          }}
          onBlur={commitDraft}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commitDraft();
              e.currentTarget.blur();
            }
            if (e.key === 'Escape') {
              setDraft('');
              setIsInvalid(false);
              e.currentTarget.blur();
            }
          }}
          className={`w-full border px-2 py-1.5 text-sm focus:outline-none placeholder:text-gray-400 ${
            isInvalid
              ? 'border-red-500 bg-red-50 text-red-700 focus:border-red-600'
              : 'border-gray-300 focus:border-gray-900'
          }`}
        />
        {percent && <span className={`text-sm font-semibold ${isInvalid ? 'text-red-700' : 'text-gray-700'}`}>%</span>}
      </div>
      {isInvalid && (
        <div className="text-[11px] font-medium text-red-600">
          {integer ? 'Valore intero richiesto' : 'Valore numerico non valido. Usa "." per i decimali'}
        </div>
      )}
    </div>
  );
}

export default function OptimizationConfigEditor({
  config,
  availableTipoFilati = [],
  availableConsegne = [],
  readOnly = false,
  onChange,
  onSave,
  isSaving = false,
}: OptimizationConfigEditorProps) {
  const [isExpectedSelloutByTfOpen, setIsExpectedSelloutByTfOpen] = useState(false);
  const [isMinAllowedByTfOpen, setIsMinAllowedByTfOpen] = useState(false);
  const [newExpectedSelloutTf, setNewExpectedSelloutTf] = useState('');
  const [newExpectedSelloutValue, setNewExpectedSelloutValue] = useState('');
  const [newMinAllowedTf, setNewMinAllowedTf] = useState('');
  const [newMinAllowedValue, setNewMinAllowedValue] = useState('');
  const [newConsegnaPriorityTf, setNewConsegnaPriorityTf] = useState('');

  const objective = config?.objective || {};
  const preparation = config?.preparation || {};
  const solver = config?.solver || {};
  const constraints = config?.constraints || {};

  const activeConstraintEntries = useMemo(
    () => (Object.entries(constraints.active_constraints || {}) as Array<[string, boolean]>)
      .filter(([name]) => !HIDDEN_ACTIVE_CONSTRAINTS.has(name)),
    [constraints.active_constraints],
  );

  const minAllowedByTfEntries = useMemo(
    () => Object.entries(constraints.min_allowed_value_per_tipo_filato || {}) as Array<[string, number]>,
    [constraints.min_allowed_value_per_tipo_filato],
  );

  const expectedSelloutByTfEntries = useMemo(
    () => Object.entries(preparation.expected_sellout_per_tipo_filato || {}) as Array<[string, number]>,
    [preparation.expected_sellout_per_tipo_filato],
  );

  const consegnaPriorityEntries = useMemo(
    () => Object.entries(constraints.consegna_priority_by_tipo_filato || {}) as Array<[string, Record<string, ConsegnaPriorityState>]>,
    [constraints.consegna_priority_by_tipo_filato],
  );

  const tipoFilatoOptions = useMemo(
    () => Array.from(new Set(availableTipoFilati.filter(Boolean))).sort((a, b) => a.localeCompare(b)),
    [availableTipoFilati],
  );

  const commit = async (next: any) => {
    onChange(next);
    if (onSave) {
      try {
        await onSave(next);
      } catch (err) {
        console.error('Error saving config from editor:', err);
      }
    }
  };

  const updateConsegnaPriorityCell = async (tipoFilato: string, consegna: string) => {
    const next = cloneConfig(config);
    if (!next.constraints) next.constraints = {};
    if (!next.constraints.active_constraints) next.constraints.active_constraints = {};
    if (!next.constraints.consegna_priority_by_tipo_filato) next.constraints.consegna_priority_by_tipo_filato = {};
    const currentState = next.constraints.consegna_priority_by_tipo_filato?.[tipoFilato]?.[consegna];
    const nextState = nextConsegnaPriorityState(currentState);
    if (!next.constraints.consegna_priority_by_tipo_filato[tipoFilato]) next.constraints.consegna_priority_by_tipo_filato[tipoFilato] = {};
    if (nextState === 'neutral') {
      delete next.constraints.consegna_priority_by_tipo_filato[tipoFilato][consegna];
    } else {
      next.constraints.consegna_priority_by_tipo_filato[tipoFilato][consegna] = nextState;
    }
    if (Object.keys(next.constraints.consegna_priority_by_tipo_filato[tipoFilato]).length === 0) {
      delete next.constraints.consegna_priority_by_tipo_filato[tipoFilato];
    }
    next.constraints.active_constraints.consegna_priority = Object.keys(next.constraints.consegna_priority_by_tipo_filato).length > 0;
    if (!next.objective) next.objective = {};
    if (next.objective.lambda_consegna_priority === undefined) next.objective.lambda_consegna_priority = 1;
    await commit(next);
  };

  // SPJ-946: trimestre effettivo mostrato per una consegna — se la config ha un mapping
  // esplicito fa fede (assente = peso 0), altrimenti si mostrano i suggerimenti.
  const configuredForecastQuarters = (constraints.consegna_forecast_quarter_by_consegna && typeof constraints.consegna_forecast_quarter_by_consegna === 'object')
    ? constraints.consegna_forecast_quarter_by_consegna as Record<string, number>
    : {};
  const hasExplicitForecastQuarters = Object.keys(configuredForecastQuarters).length > 0;
  const getEffectiveForecastQuarter = (consegna: string): number | undefined => {
    const cleaned = consegna.trim().toLowerCase();
    if (hasExplicitForecastQuarters) {
      const entry = Object.entries(configuredForecastQuarters).find(([name]) => name.trim().toLowerCase() === cleaned);
      const value = entry ? Number(entry[1]) : NaN;
      return value >= 1 && value <= 4 ? value : undefined;
    }
    return FORECAST_QUARTER_SUGGESTIONS[cleaned];
  };
  const updateForecastQuarter = async (consegna: string, rawQuarter: string) => {
    const next = cloneConfig(config);
    if (!next.constraints) next.constraints = {};
    // Materializza il mapping effettivo per tutte le consegne, così il primo edit
    // congela anche i suggerimenti correnti (la config diventa autoritativa).
    const effective: Record<string, number> = {};
    availableConsegne.forEach((name) => {
      const value = getEffectiveForecastQuarter(name);
      if (value !== undefined) effective[name] = value;
    });
    if (rawQuarter === '') {
      delete effective[consegna];
    } else {
      effective[consegna] = Number(rawQuarter);
    }
    next.constraints.consegna_forecast_quarter_by_consegna = effective;
    await commit(next);
  };

  const updateConsegnaPriorityEnabled = async (enabled: boolean) => {
    const next = cloneConfig(config);
    if (!next.constraints) next.constraints = {};
    if (!next.constraints.active_constraints) next.constraints.active_constraints = {};
    next.constraints.active_constraints.consegna_priority = enabled;
    await commit(next);
  };

  const setConsegnaPriorityState = async (tipoFilato: string, consegna: string, state: ConsegnaPriorityState) => {
    const next = cloneConfig(config);
    if (!next.constraints) next.constraints = {};
    if (!next.constraints.active_constraints) next.constraints.active_constraints = {};
    if (!next.constraints.consegna_priority_by_tipo_filato) next.constraints.consegna_priority_by_tipo_filato = {};
    if (!next.constraints.consegna_priority_by_tipo_filato[tipoFilato]) next.constraints.consegna_priority_by_tipo_filato[tipoFilato] = {};
    // Keep explicit neutral values for manually added TF rows so the row does
    // not disappear while the user is editing it. "Presente" remains a no-op
    // for the solver, but it is part of the UI draft until removed explicitly.
    next.constraints.consegna_priority_by_tipo_filato[tipoFilato][consegna] = state;
    next.constraints.active_constraints.consegna_priority = Object.keys(next.constraints.consegna_priority_by_tipo_filato).length > 0;
    if (!next.objective) next.objective = {};
    if (next.objective.lambda_consegna_priority === undefined) next.objective.lambda_consegna_priority = 1;
    await commit(next);
  };

  const addConsegnaPriorityTipoFilato = async () => {
    const tipoFilato = newConsegnaPriorityTf.trim();
    if (!tipoFilato) return;
    const next = cloneConfig(config);
    if (!next.constraints) next.constraints = {};
    if (!next.constraints.active_constraints) next.constraints.active_constraints = {};
    if (!next.constraints.consegna_priority_by_tipo_filato) next.constraints.consegna_priority_by_tipo_filato = {};
    if (!next.constraints.consegna_priority_by_tipo_filato[tipoFilato]) {
      next.constraints.consegna_priority_by_tipo_filato[tipoFilato] = Object.fromEntries(
        availableConsegne.map((consegna) => [consegna, 'neutral' as ConsegnaPriorityState]),
      );
    }
    next.constraints.active_constraints.consegna_priority = true;
    if (!next.objective) next.objective = {};
    if (next.objective.lambda_consegna_priority === undefined) next.objective.lambda_consegna_priority = 1;
    await commit(next);
    setNewConsegnaPriorityTf('');
  };

  const removeConsegnaPriorityTipoFilato = async (tipoFilato: string) => {
    const next = cloneConfig(config);
    if (next.constraints?.consegna_priority_by_tipo_filato) {
      delete next.constraints.consegna_priority_by_tipo_filato[tipoFilato];
      if (!next.constraints.active_constraints) next.constraints.active_constraints = {};
      next.constraints.active_constraints.consegna_priority = Object.keys(next.constraints.consegna_priority_by_tipo_filato).length > 0;
    }
    await commit(next);
  };

  const updateExpectedSelloutPerTipoFilato = async (tipoFilato: string, value: number) => {
    const next = cloneConfig(config);
    if (!next.preparation) next.preparation = {};
    if (!next.preparation.expected_sellout_per_tipo_filato) {
      next.preparation.expected_sellout_per_tipo_filato = {};
    }
    next.preparation.expected_sellout_per_tipo_filato[tipoFilato] = value;
    await commit(next);
  };

  const removeExpectedSelloutPerTipoFilato = async (tipoFilato: string) => {
    const next = cloneConfig(config);
    if (next.preparation?.expected_sellout_per_tipo_filato) {
      delete next.preparation.expected_sellout_per_tipo_filato[tipoFilato];
      if (Object.keys(next.preparation.expected_sellout_per_tipo_filato).length === 0) {
        delete next.preparation.expected_sellout_per_tipo_filato;
      }
    }
    await commit(next);
  };

  const addExpectedSelloutPerTipoFilato = async () => {
    if (!newExpectedSelloutTf.trim() || newExpectedSelloutValue.trim() === '') return;
    const parsed = parseStrictNumber(newExpectedSelloutValue, { percent: true });
    if (parsed === null) return;
    await updateExpectedSelloutPerTipoFilato(newExpectedSelloutTf.trim(), parsed);
    setNewExpectedSelloutTf('');
    setNewExpectedSelloutValue('');
    setIsExpectedSelloutByTfOpen(true);
  };

  const updateMinAllowedPerTipoFilato = async (tipoFilato: string, value: number) => {
    const next = cloneConfig(config);
    if (!next.constraints) next.constraints = {};
    if (!next.constraints.min_allowed_value_per_tipo_filato) {
      next.constraints.min_allowed_value_per_tipo_filato = {};
    }
    next.constraints.min_allowed_value_per_tipo_filato[tipoFilato] = Math.trunc(value);
    await commit(next);
  };

  const removeMinAllowedPerTipoFilato = async (tipoFilato: string) => {
    const next = cloneConfig(config);
    if (next.constraints?.min_allowed_value_per_tipo_filato) {
      delete next.constraints.min_allowed_value_per_tipo_filato[tipoFilato];
      if (Object.keys(next.constraints.min_allowed_value_per_tipo_filato).length === 0) {
        delete next.constraints.min_allowed_value_per_tipo_filato;
      }
    }
    await commit(next);
  };

  const addMinAllowedPerTipoFilato = async () => {
    if (!newMinAllowedTf.trim() || newMinAllowedValue.trim() === '') return;
    const parsed = parseStrictNumber(newMinAllowedValue, { integer: true });
    if (parsed === null) return;
    await updateMinAllowedPerTipoFilato(newMinAllowedTf.trim(), parsed);
    setNewMinAllowedTf('');
    setNewMinAllowedValue('');
  };

  return (
    <div className="space-y-4">
      <div className="grid gap-4 lg:grid-cols-3">
        <section className="border border-gray-200 bg-white p-4">
          <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-700">Objective</h3>
          <div className="space-y-2">
            {OBJECTIVE_KEYS.map((key) => {
              if (objective[key] === undefined) return null;
              return (
                <label key={key} className="block text-xs text-gray-600">
                  <span className="mb-1 block font-medium uppercase tracking-wide">{key}</span>
                  <NumericSuggestionInput
                    currentValue={Number(objective[key])}
                    disabled={readOnly}
                    onCommit={(value) => {
                      const next = cloneConfig(config);
                      if (!next.objective) next.objective = {};
                      next.objective[key] = value;
                      return commit(next);
                    }}
                  />
                </label>
              );
            })}
          </div>
        </section>

        <section className="border border-gray-200 bg-white p-4">
          <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-700">Preparation</h3>
          <div className="space-y-3">
            {preparation.expected_sellout !== undefined && (
              <label className="block text-xs text-gray-600">
                <span className="mb-1 flex items-center justify-between gap-3 font-medium uppercase tracking-wide">
                  <span>expected_sellout</span>
                </span>
                <NumericSuggestionInput
                  currentValue={Number(preparation.expected_sellout || 0)}
                  percent
                  disabled={readOnly}
                  onCommit={(value) => {
                    const next = cloneConfig(config);
                    if (!next.preparation) next.preparation = {};
                    next.preparation.expected_sellout = value;
                    return commit(next);
                  }}
                />
              </label>
            )}

            {(tipoFilatoOptions.length > 0 || expectedSelloutByTfEntries.length > 0) && (
              <div className="border border-gray-200">
                <button
                  type="button"
                  onClick={() => setIsExpectedSelloutByTfOpen((prev) => !prev)}
                  className="flex w-full items-center justify-between px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-700"
                >
                  <span>expected_sellout_per_tipo_filato</span>
                  <span>{isExpectedSelloutByTfOpen ? '−' : '+'}</span>
                </button>

                {isExpectedSelloutByTfOpen && (
                  <div className="space-y-2 border-t border-gray-200 p-3">
                    {expectedSelloutByTfEntries.map(([name, value]) => (
                      <div key={name} className="rounded border border-gray-200 px-3 py-2 text-xs text-gray-600">
                        <div className="mb-2 flex items-center justify-between gap-2">
                          <span className="font-medium uppercase tracking-wide text-gray-700">{name}</span>
                          <button
                            type="button"
                            disabled={readOnly}
                            onClick={() => {
                              void removeExpectedSelloutPerTipoFilato(name);
                            }}
                            className="border border-red-200 px-2 py-1 text-[11px] font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50"
                          >
                            Rimuovi
                          </button>
                        </div>
                        <label className="block">
                          <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-gray-500">Percentuale</span>
                          <NumericSuggestionInput
                            currentValue={Number(value || 0)}
                            percent
                            disabled={readOnly}
                            onCommit={(nextValue) => updateExpectedSelloutPerTipoFilato(name, nextValue)}
                          />
                        </label>
                      </div>
                    ))}

                    {!readOnly && (
                      <div className="rounded border border-dashed border-gray-300 p-3">
                        {/* <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">Aggiungi</div> */}
                        <div className="grid gap-2 md:grid-cols-[minmax(0,1fr)_120px_auto]">
                          <select
                            value={newExpectedSelloutTf}
                            onChange={(e) => setNewExpectedSelloutTf(e.target.value)}
                            className="border border-gray-300 px-2 py-1.5 text-sm focus:border-gray-900 focus:outline-none"
                          >
                            <option value="">Seleziona tipo filato</option>
                            {tipoFilatoOptions.map((tipoFilato) => (
                              <option key={tipoFilato} value={tipoFilato}>{tipoFilato}</option>
                            ))}
                          </select>
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              inputMode="decimal"
                              value={newExpectedSelloutValue}
                              onChange={(e) => setNewExpectedSelloutValue(e.target.value)}
                              placeholder="70"
                              className="w-full border border-gray-300 px-2 py-1.5 text-sm focus:border-gray-900 focus:outline-none placeholder:text-gray-400"
                            />
                            <span className="text-sm font-semibold text-gray-700">%</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              void addExpectedSelloutPerTipoFilato();
                            }}
                            className="border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:border-gray-900 hover:bg-gray-50"
                          >
                            Aggiungi
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {preparation.force_first_three_taglie !== undefined && (
              <label className="flex items-center justify-between text-sm text-gray-700">
                <span>force_first_three_taglie</span>
                <input
                  type="checkbox"
                  checked={Boolean(preparation.force_first_three_taglie)}
                  disabled={readOnly}
                  onChange={(e) => {
                    const next = cloneConfig(config);
                    if (!next.preparation) next.preparation = {};
                    next.preparation.force_first_three_taglie = e.target.checked;
                    void commit(next);
                  }}
                />
              </label>
            )}

            {preparation.scale_orders !== undefined && (
              <label className="flex items-center justify-between text-sm text-gray-700">
                <span>scale_orders</span>
                <input
                  type="checkbox"
                  checked={Boolean(preparation.scale_orders)}
                  disabled={readOnly}
                  onChange={(e) => {
                    const next = cloneConfig(config);
                    if (!next.preparation) next.preparation = {};
                    next.preparation.scale_orders = e.target.checked;
                    void commit(next);
                  }}
                />
              </label>
            )}

            {preparation.taglie_sampling_method !== undefined && (
              <label className="block text-xs text-gray-600">
                <span className="mb-1 block font-medium uppercase tracking-wide">taglie_sampling_method</span>
                <select
                  value={String(preparation.taglie_sampling_method)}
                  disabled={readOnly}
                  onChange={(e) => {
                    const next = cloneConfig(config);
                    if (!next.preparation) next.preparation = {};
                    next.preparation.taglie_sampling_method = e.target.value;
                    void commit(next);
                  }}
                  className="w-full border border-gray-300 px-2 py-1.5 text-sm focus:border-gray-900 focus:outline-none"
                >
                  <option value="legacy">legacy</option>
                  <option value="hare_niemeyer">hare_niemeyer</option>
                </select>
              </label>
            )}
          </div>
        </section>

        <section className="border border-gray-200 bg-white p-4">
          <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-700">Solver</h3>
          <div className="space-y-2">
            {solver.type !== undefined && (
              <label className="block text-xs text-gray-600">
                <span className="mb-1 block font-medium uppercase tracking-wide">type</span>
                <select
                  value={String(solver.type)}
                  disabled={readOnly}
                  onChange={(e) => {
                    const next = cloneConfig(config);
                    if (!next.solver) next.solver = {};
                    next.solver.type = e.target.value;
                    void commit(next);
                  }}
                  className="w-full border border-gray-300 px-2 py-1.5 text-sm focus:border-gray-900 focus:outline-none"
                >
                  <option value="CBC">CBC</option>
                  <option value="CPSAT">CPSAT</option>
                </select>
              </label>
            )}

            {solver.time_limit !== undefined && (
              <label className="block text-xs text-gray-600">
                <span className="mb-1 block font-medium uppercase tracking-wide">time_limit</span>
                <NumericSuggestionInput
                  currentValue={Number(solver.time_limit)}
                  integer
                  disabled={readOnly}
                  onCommit={(value) => {
                    const next = cloneConfig(config);
                    if (!next.solver) next.solver = {};
                    next.solver.time_limit = Math.trunc(value);
                    return commit(next);
                  }}
                />
              </label>
            )}

            {solver.relative_gap !== undefined && (
              <label className="block text-xs text-gray-600">
                <span className="mb-1 block font-medium uppercase tracking-wide">relative_gap</span>
                <NumericSuggestionInput
                  currentValue={solver.relative_gap}
                  disabled={readOnly}
                  onCommit={(value) => {
                    const next = cloneConfig(config);
                    if (!next.solver) next.solver = {};
                    next.solver.relative_gap = value;
                    return commit(next);
                  }}
                />
              </label>
            )}

            {solver.num_workers !== undefined && (
              <label className="block text-xs text-gray-600">
                <span className="mb-1 block font-medium uppercase tracking-wide">num_workers</span>
                <NumericSuggestionInput
                  currentValue={solver.num_workers}
                  integer
                  disabled={readOnly}
                  onCommit={(value) => {
                    const next = cloneConfig(config);
                    if (!next.solver) next.solver = {};
                    next.solver.num_workers = Math.trunc(value);
                    return commit(next);
                  }}
                />
              </label>
            )}
          </div>
        </section>
      </div>

      <section className="border border-gray-200 bg-white p-4">
        <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-700">Constraints</h3>

        <div className="grid gap-4 lg:grid-cols-2">
          <div className="space-y-2">
            {CONSTRAINT_NUMERIC_KEYS.map((key) => {
              if (constraints[key] === undefined) return null;
              return (
                <div key={key} className="space-y-2">
                  <label className="block text-xs text-gray-600">
                    <span className="mb-1 block font-medium uppercase tracking-wide">{key}</span>
                    <NumericSuggestionInput
                      currentValue={Number(constraints[key])}
                      integer={isIntegerConstraintKey(key)}
                      disabled={readOnly}
                      onCommit={(value) => {
                        const next = cloneConfig(config);
                        if (!next.constraints) next.constraints = {};
                        next.constraints[key] = isIntegerConstraintKey(key) ? Math.trunc(value) : value;
                        return commit(next);
                      }}
                    />
                  </label>

                  {key === 'min_allowed_value' && (tipoFilatoOptions.length > 0 || minAllowedByTfEntries.length > 0) && (
                    <div className="border border-gray-200">
                      <button
                        type="button"
                        onClick={() => setIsMinAllowedByTfOpen((prev) => !prev)}
                        className="flex w-full items-center justify-between px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-700"
                      >
                        <span>min_allowed_value_per_tipo_filato</span>
                        <span>{isMinAllowedByTfOpen ? '−' : '+'}</span>
                      </button>

                      {isMinAllowedByTfOpen && (
                        <div className="space-y-2 border-t border-gray-200 p-3">
                          {minAllowedByTfEntries.map(([name, value]) => (
                            <div key={name} className="rounded border border-gray-200 px-3 py-2 text-xs text-gray-600">
                              <div className="mb-2 flex items-center justify-between gap-2">
                                <span className="font-medium uppercase tracking-wide text-gray-700">{name}</span>
                                <button
                                  type="button"
                                  disabled={readOnly}
                                  onClick={() => {
                                    void removeMinAllowedPerTipoFilato(name);
                                  }}
                                  className="border border-red-200 px-2 py-1 text-[11px] font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50"
                                >
                                  Rimuovi
                                </button>
                              </div>
                              <NumericSuggestionInput
                                currentValue={Number(value)}
                                integer
                                disabled={readOnly}
                                onCommit={(nextValue) => updateMinAllowedPerTipoFilato(name, nextValue)}
                              />
                            </div>
                          ))}

                          {!readOnly && (
                            <div className="rounded border border-dashed border-gray-300 p-3">
                              <div className="grid gap-2 md:grid-cols-[minmax(0,1fr)_120px_auto]">
                                <select
                                  value={newMinAllowedTf}
                                  onChange={(e) => setNewMinAllowedTf(e.target.value)}
                                  className="border border-gray-300 px-2 py-1.5 text-sm focus:border-gray-900 focus:outline-none"
                                >
                                  <option value="">Seleziona tipo filato</option>
                                  {tipoFilatoOptions.map((tipoFilato) => (
                                    <option key={tipoFilato} value={tipoFilato}>{tipoFilato}</option>
                                  ))}
                                </select>
                                <input
                                  type="text"
                                  inputMode="numeric"
                                  value={newMinAllowedValue}
                                  onChange={(e) => setNewMinAllowedValue(e.target.value)}
                                  placeholder="3"
                                  className="w-full border border-gray-300 px-2 py-1.5 text-sm focus:border-gray-900 focus:outline-none placeholder:text-gray-400"
                                />
                                <button
                                  type="button"
                                  onClick={() => {
                                    void addMinAllowedPerTipoFilato();
                                  }}
                                  className="border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:border-gray-900 hover:bg-gray-50"
                                >
                                  Aggiungi
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}

            {tipoFilatoOptions.length > 0 && availableConsegne.length > 0 && (
              <div className="border border-gray-200 bg-white p-3">
                <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Priorità consegne per tipo filato</div>
                    <div className="mt-1 text-xs text-gray-500">
                      Di default ogni tipo filato è presente in ogni consegna. Aggiungi solo i tipi filato per cui vuoi una preferenza soft.
                    </div>
                  </div>
                  <label className="inline-flex items-center gap-2 text-xs font-semibold text-gray-700">
                    <input
                      type="checkbox"
                      checked={Boolean(constraints.active_constraints?.consegna_priority)}
                      disabled={readOnly}
                      onChange={(event) => { void updateConsegnaPriorityEnabled(event.target.checked); }}
                    />
                    Attiva
                  </label>
                </div>

                <div className="space-y-3">
                  {consegnaPriorityEntries.length === 0 && (
                    <div className="rounded border border-gray-200 bg-gray-50 p-3 text-xs text-gray-500">
                      Nessun tipo filato configurato: tutti sono considerati <strong>Presenti</strong> in tutte le consegne.
                    </div>
                  )}

                  {consegnaPriorityEntries.map(([tipoFilato, byConsegna]) => (
                    <div key={tipoFilato} className="rounded border border-gray-200 p-3">
                      <div className="mb-3 flex items-center justify-between gap-3">
                        <div className="text-sm font-semibold text-gray-900">{tipoFilato}</div>
                        {!readOnly && (
                          <button
                            type="button"
                            onClick={() => { void removeConsegnaPriorityTipoFilato(tipoFilato); }}
                            className="text-xs font-semibold uppercase tracking-wide text-red-600 hover:text-red-800"
                          >
                            Rimuovi
                          </button>
                        )}
                      </div>
                      <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-4">
                        {availableConsegne.map((consegna) => {
                          const state = normalizeConsegnaPriorityState(byConsegna?.[consegna]);
                          return (
                            <label key={`${tipoFilato}:${consegna}`} className="flex flex-col gap-1 text-xs font-semibold text-gray-600">
                              {consegna}
                              <select
                                value={state}
                                disabled={readOnly}
                                onChange={(event) => { void setConsegnaPriorityState(tipoFilato, consegna, event.target.value as ConsegnaPriorityState); }}
                                className={`border px-2 py-1.5 text-xs font-semibold uppercase tracking-wide focus:outline-none ${CONSEGNA_PRIORITY_CLASSES[state]}`}
                              >
                                <option value="neutral">Presente</option>
                                <option value="prioritized">Prioritizzato</option>
                                <option value="avoid">Evita</option>
                              </select>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  ))}

                  {!readOnly && (
                    <div className="rounded border border-dashed border-gray-300 p-3">
                      <div className="grid gap-2 md:grid-cols-[minmax(0,1fr)_auto]">
                        <select
                          value={newConsegnaPriorityTf}
                          onChange={(e) => setNewConsegnaPriorityTf(e.target.value)}
                          className="border border-gray-300 px-2 py-1.5 text-sm focus:border-gray-900 focus:outline-none"
                        >
                          <option value="">Seleziona tipo filato</option>
                          {tipoFilatoOptions
                            .filter((tipoFilato) => !constraints.consegna_priority_by_tipo_filato?.[tipoFilato])
                            .map((tipoFilato) => (
                              <option key={tipoFilato} value={tipoFilato}>{tipoFilato}</option>
                            ))}
                        </select>
                        <button
                          type="button"
                          onClick={() => { void addConsegnaPriorityTipoFilato(); }}
                          className="border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:border-gray-900 hover:bg-gray-50"
                        >
                          Aggiungi
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="space-y-4">


            {activeConstraintEntries.length > 0 && (
              <div>
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">active_constraints</div>
                <div className="space-y-1">
                  {activeConstraintEntries.map(([name, value]) => (
                    <label key={name} className="flex items-start justify-between gap-3 border border-gray-200 px-2 py-2 text-sm text-gray-700">
                      <span className="min-w-0">
                        <span className="block font-medium">{name}</span>
                        {ACTIVE_CONSTRAINT_DESCRIPTIONS[name] && (
                          <span className="mt-0.5 block text-xs leading-snug text-gray-500">
                            {ACTIVE_CONSTRAINT_DESCRIPTIONS[name]}
                          </span>
                        )}
                      </span>
                      <input
                        type="checkbox"
                        checked={Boolean(value)}
                        disabled={readOnly}
                        onChange={(e) => {
                          const next = cloneConfig(config);
                          if (!next.constraints) next.constraints = {};
                          if (!next.constraints.active_constraints) next.constraints.active_constraints = {};
                          next.constraints.active_constraints[name] = e.target.checked;
                          // budget_target_soft: garantisce una lambda editabile (default 1) all'attivazione
                          if (name === 'budget_target_soft' && e.target.checked) {
                            if (!next.objective) next.objective = {};
                            if (next.objective.lambda_budget_target === undefined) next.objective.lambda_budget_target = 1;
                          }
                          void commit(next);
                        }}
                        className="mt-1 shrink-0"
                      />
                    </label>
                  ))}
                </div>
              </div>
            )}

            {Boolean((constraints.active_constraints || {}).consegna_forecast_priority) && availableConsegne.length > 0 && (
              <div className="border border-gray-200 bg-white p-3">
                <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">Trimestri forecast per consegna</div>
                <div className="mb-2 text-xs leading-snug text-gray-500">
                  Assegna a ogni consegna il trimestre di predizione (finestre di 3 mesi dalla prima data di forecast).
                  Le consegne senza trimestre hanno peso 0. Per il layout Spring/Resort i valori proposti sono i suggerimenti standard.
                </div>
                <div className="space-y-1">
                  {availableConsegne.map((consegna) => {
                    const effective = getEffectiveForecastQuarter(consegna);
                    return (
                      <div key={consegna} className="flex items-center justify-between gap-3 border border-gray-200 px-2 py-1.5 text-sm text-gray-700">
                        <span className="min-w-0 flex-1 truncate font-medium" title={consegna}>{consegna}</span>
                        {!hasExplicitForecastQuarters && effective !== undefined && (
                          <span className="shrink-0 text-[10px] uppercase tracking-wide text-gray-400">suggerito</span>
                        )}
                        <select
                          value={effective !== undefined ? String(effective) : ''}
                          disabled={readOnly}
                          onChange={(e) => {
                            void updateForecastQuarter(consegna, e.target.value);
                          }}
                          className="shrink-0 border border-gray-300 px-2 py-1 text-sm focus:border-gray-900 focus:outline-none disabled:opacity-50"
                        >
                          <option value="">— (peso 0)</option>
                          {FORECAST_QUARTER_OPTIONS.map((quarter) => (
                            <option key={quarter} value={quarter}>{`Q${quarter}`}</option>
                          ))}
                        </select>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

          </div>
        </div>
      </section>

      {isSaving && <div className="text-sm text-gray-500">Salvataggio vincoli…</div>}
    </div>
  );
}
